import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { SharePointFileBrowser } from '../components/SharePointFileBrowser.jsx';
import { downloadPdfDocument } from '../components/PdfDocumentsPanel.jsx';
import { useAuth } from '../context/AuthContext.jsx';

export function MyFilesPage() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [searchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim().toLowerCase();
  const [data, setData] = useState(null);
  const [pdfs, setPdfs] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      apiJson('/api/my/files'),
      apiJson('/api/my/pdf-documents').catch(() => []),
    ])
      .then(([files, sharedPdfs]) => {
        setData(files);
        setPdfs(sharedPdfs);
      })
      .catch((e) => setError(e.message));
  }, []);

  const openPdf = async (doc) => {
    try {
      const blob = await downloadPdfDocument(doc.id, 'my');
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e.message);
    }
  };

  const match = (value) => !query || String(value || '').toLowerCase().includes(query);
  const projects = useMemo(
    () => (data?.projects || []).filter((p) => match(p.name)),
    [data, query],
  );
  const visiblePdfs = useMemo(
    () => pdfs.filter((d) => match(d.title)),
    [pdfs, query],
  );
  const documents = useMemo(
    () => (data?.documents || []).filter((d) => match(d.label) || match(d.url)),
    [data, query],
  );

  if (error) return <p className="err">{error}</p>;
  if (!data) return <p className="muted">Loading files…</p>;

  return (
    <div>
      <h2 style={{ marginTop: 0 }} data-tour="page-title">Files</h2>
      <p className="muted small">SharePoint / Teams folders, shared PDF documents, and document links.</p>
      <div className="auth-card flat" data-tour="files-list">
        <h3>Project folders</h3>
        {projects.length ? (
          <ul>
            {projects.map((p) => (
              <li key={p.id}>
                <Link to={`/my/projects/${p.id}`}>{p.name}</Link>
                {' · '}
                <a href={p.teams_files_web_url} target="_blank" rel="noreferrer">
                  Open in SharePoint / Teams
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No project folders linked yet.</p>
        )}
      </div>
      {caps.view_sharepoint ? (
        <div className="auth-card flat" style={{ marginTop: '1rem' }}>
          <h3>Browse SharePoint</h3>
          <SharePointFileBrowser apiBase="/api/my/sharepoint/browse" />
        </div>
      ) : null}
      <div className="auth-card flat" style={{ marginTop: '1rem' }}>
        <div className="page-header-row" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Shared PDFs</h3>
          <Link to="/my/pdf-documents" className="btn btn-sm btn-outline-secondary">Open all</Link>
        </div>
        {visiblePdfs.length ? (
          <ul>
            {visiblePdfs.map((d) => (
              <li key={d.id}>
                <button type="button" className="btn btn-link p-0" onClick={() => void openPdf(d)}>
                  {d.title}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No shared PDFs yet.</p>
        )}
      </div>
      <div className="auth-card flat" style={{ marginTop: '1rem' }}>
        <h3>Shared documents</h3>
        {documents.length ? (
          <ul>
            {documents.map((d) => (
              <li key={d.id}>
                <a href={d.url} target="_blank" rel="noreferrer">
                  {d.label}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No document links yet.</p>
        )}
      </div>
    </div>
  );
}
